import React, { Dispatch, SetStateAction } from 'react'
import { FilterState } from '@pages/ApprovalRequests/RequestsList/Filter/type'
import { Status, SortOrder } from '@type/common'

/**
 * Per-workflow column preferences. Both `order` and `hidden` are intentionally
 * graceful: columns missing from `order` are appended at the end in their
 * default position (so newly-added workflow fields remain visible), and IDs in
 * `order`/`hidden` that no longer exist are simply ignored.
 */
export interface ColumnPreference {
  order?: string[]
  hidden?: string[]
}

/** Keyed by workflow id; the special key `default` is used when no workflow is selected. */
export type ColumnPreferences = Record<string, ColumnPreference>

export const DEFAULT_COLUMN_PREFS_KEY = 'default'

/**
 * Lightweight description of the columns currently rendered by the table. Published
 * by `TableView` so that UI rendered higher up in the tree (e.g. the page header
 * Columns trigger) can manage them without prop-drilling react-table column defs.
 */
export interface ColumnMetadata {
  id: string
  label: string
}

interface DefaultProps {
  filters: FilterState
  sorting: Record<string, SortOrder>
  areFiltersCollapsed: boolean
  selectedRequestIds: { id: number; status: Status }[]
  columnPreferences: ColumnPreferences
  columnsMetadata: ColumnMetadata[]
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>
  setSorting: React.Dispatch<React.SetStateAction<Record<string, SortOrder>>>
  setSelectedRequestIds: Dispatch<SetStateAction<{ id: number; status: Status }[]>>
  setAreFiltersCollapsed: Dispatch<SetStateAction<boolean>>
  setColumnPreferences: Dispatch<SetStateAction<ColumnPreferences>>
  setColumnsMetadata: Dispatch<SetStateAction<ColumnMetadata[]>>
}

export const Default: DefaultProps = {
  filters: {
    status: '',
    date: {},
    completionDate: {},
    workflow: '',
    pipelineProcessName: '',
    actionRequired: false,
    requestor: '',
    approver: '',
    search: '',
    page: 1,
    limit: 1,
  },
  sorting: {},
  areFiltersCollapsed: false,
  selectedRequestIds: [],
  columnPreferences: {},
  columnsMetadata: [],
  setFilters: () => null,
  setSorting: () => null,
  setSelectedRequestIds: () => null,
  setAreFiltersCollapsed: () => null,
  setColumnPreferences: () => null,
  setColumnsMetadata: () => null,
}

export const Context = React.createContext(Default)

export default Context
